import { isAdAvailable } from './anuncios'
import { ProductCombinationAttributeSlug } from './enums/product-combination-attribute-slug'

const getUniqueAttributesSlugs = (attributes) => {
  const treatedAttributes = attributes.map(
    ({ available, image, valor_slug }) => ({
      available,
      image,
      slug: valor_slug,
    })
  )

  return treatedAttributes.filter(
    (elem, index, arr) => arr.findIndex((e) => e.slug === elem.slug) === index
  )
}

const getAttributeValueBySlug = (slug, attributes) => {
  return attributes.find((attribute) => attribute.valor_slug === slug)?.valor
}

const checkIfTheVariationIsAvailable = (variation) => {
  return variation.anuncios.some((ad) => isAdAvailable(ad) && ad.situacao)
}

export const getUniqueVoltagesFromProductVariations = (productVariations) => {
  const combinations = productVariations
    .map((variation) =>
      variation.combinacoes.map((combination) => ({
        ...combination,
        available: checkIfTheVariationIsAvailable(variation),
        image: variation.imagens?.[0]?.url,
      }))
    )
    .flat()
    .sort((value) => (value.available ? -1 : 1))

  const attributes = combinations.filter(
    (combination) =>
      combination.atributo_slug === ProductCombinationAttributeSlug.Voltage
  )

  return getUniqueAttributesSlugs(attributes).map(
    ({ available, image, slug }) => ({
      available,
      image,
      slug,
      valor: getAttributeValueBySlug(slug, attributes),
    })
  )
}

const filterVariationsByCombination = (attributeSlug, slug, variations) => {
  const isValidCombination = (combination) =>
    combination.atributo_slug === attributeSlug &&
    combination.valor_slug === slug

  return variations.filter((variation) =>
    variation.combinacoes.some(isValidCombination)
  )
}

export const getUniqueAvailableColorsToSelectedVoltage = (
  voltage,
  productVariations
) => {
  const availableVariations = filterVariationsByCombination(
    ProductCombinationAttributeSlug.Voltage,
    voltage,
    productVariations
  )

  const combinations = availableVariations
    .map((variation) =>
      variation.combinacoes.map((combination) => ({
        ...combination,
        available: checkIfTheVariationIsAvailable(variation),
        image: variation.imagens?.[0]?.url,
      }))
    )
    .flat()
    .sort((value) => (value.available ? -1 : 1))

  const availableColors = combinations.filter(
    (combination) =>
      combination.atributo_slug === ProductCombinationAttributeSlug.Color
  )

  return getUniqueAttributesSlugs(availableColors).map(
    ({ available, image, slug }) => ({
      available,
      image,
      slug,
      valor: getAttributeValueBySlug(slug, availableColors),
    })
  )
}

export const isAttributeActive = (attribute, activeAttribute) =>
  attribute.slug === activeAttribute

const PRODUCT_VOLTAGE_INDEX = 1

export const getSelectedVoltageFromAvailableVariations = (
  availableVariations,
  selectedVoltage
) =>
  availableVariations.find(
    (productVariation) =>
      productVariation.combinacoes?.[PRODUCT_VOLTAGE_INDEX]?.valor_slug ===
      selectedVoltage.slug
  )

export const getActiveSKUFromShowcaseProduct = (product) => {
  const activeColorSlug = product.details?.cor_id
  const activeVoltageSlug = product.details?.voltagem_id

  if (!activeColorSlug && !activeVoltageSlug) return product.skus?.[0]

  const activeVariation = product.skus?.find(
    (productVariation) =>
      productVariation.specs?.cor === activeColorSlug &&
      productVariation.specs?.voltagem === activeVoltageSlug
  )

  return activeVariation
}

export const getActiveSKUFromSearchProduct = (product) => {
  const activeColor = product.specs?.cor?.[0]?.label
  const activeVoltage = product.specs?.voltagem?.[0]?.label

  if (!activeColor && !activeVoltage) return product.skus?.[0]

  const activeVariation = product.skus?.find(
    (productVariation) =>
      productVariation.specs?.cor?.[0] === activeColor &&
      productVariation.specs?.voltagem?.[0] === activeVoltage
  )

  return activeVariation
}

const findParentCategory = (categories, parent) => {
  if (parent !== null) {
    return categories.findIndex((category) => category.parents.includes(parent))
  }
  return categories.findIndex((category) => !category.parents?.length)
}

export const getShowcaseProductCategoryId = (product) => {
  const categories = product?.categories
  let parentCategory = null

  while (categories?.length > 1) {
    const index = findParentCategory(categories, parentCategory)
    parentCategory = categories?.splice(index, 1)[0]
  }

  return categories?.[0]?.id
}

export const makeArrayQuantity = (quantity) => {
  return Array(quantity)
    .fill(0)
    .map((_, index) => ({
      label: (index + 1).toString(),
      value: index + 1,
    }))
}

export const treatedVoltageValue = (voltage) => {
  if (!voltage) return

  if (!voltage.includes('Volts')) return voltage

  const onlyNumberVoltage = voltage.replace(/[^\d.-]+/g, '')
  return `${onlyNumberVoltage}v`
}

export const descriptionFormatted = (description) => {
  const descriptionFormatted = description
    .replaceAll('<br />', ' ')
    .replaceAll('<br/>', ' ')
    .replaceAll('<br>', ' ')
    .replaceAll(':', ': ')
    .replace(/<b>.*?<\/b>/, '')
    .replace(/<[^>]*>/g, '')
    .replaceAll('&nbsp;', ' ')
    .replace('Sobre o Produto:', '')

  return descriptionFormatted
}

export const minimumAndMaximumPrice = (filterValues) => {
  const treatedFilterPrice = filterValues.filter((price) => price.size > 0)

  const firstPrice = treatedFilterPrice[0]?.min?.value

  const lastPrice =
    treatedFilterPrice[treatedFilterPrice.length - 1]?.max?.value

  return {
    min: firstPrice,
    max: lastPrice,
  }
}

export const getFilteredPrice = (selectedFilters) => {
  const filterPrice = selectedFilters?.find((filter) => filter.name === 'Preço')

  const splitedFilters = filterPrice?.value.split(':')

  if (!splitedFilters) return

  const filterPriceUrl = {
    min: parseFloat(splitedFilters?.[0]),
    max: parseFloat(splitedFilters?.[1]),
  }

  return filterPriceUrl
}

const getDeliveryTimeInHours = (store) => {
  return Number(store.prazo_dias) * 24 + Number(store.prazo_horas)
}

export const sortStoresByDeliveryTimeInHours = (storeA, storeB) => {
  const transformHoursA = getDeliveryTimeInHours(storeA)
  const transformHoursB = getDeliveryTimeInHours(storeB)

  if (transformHoursA > transformHoursB) return 1
  return transformHoursA < transformHoursB ? -1 : 0
}
